/**
 * SuperAdmin API Client
 * Axios instance configured for SuperAdmin routes
 */

import axios from 'axios';

// Mismo host de API que el resto de la app (api.finance360.cr en producción). Con la ruta
// relativa, app.finance360.cr no reenviaba /api/superadmin al backend y el login fallaba.
const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || ''}/api/superadmin`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('superadmin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Cuando la sesión venció se avisa en pantalla al volver al login.
    config.__conToken = !!token;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const vencido = error.response?.data?.code === 'TOKEN_EXPIRED';
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      if (vencido) sessionStorage.setItem('superadmin_aviso', 'La sesión venció. Vuelva a ingresar.');

      // Redirect to login if not already there
      if (!window.location.pathname.includes('/superadmin/login')) {
        window.location.href = '/superadmin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  verify: () => api.get('/auth/verify'),
  changePassword: (data) => api.post('/auth/change-password', data)
};

// Tenants API
export const tenantsApi = {
  list: (params) => api.get('/tenants', { params }),
  get: (id) => api.get(`/tenants/${id}`),
  getMetrics: (id) => api.get(`/tenants/${id}/metrics`),
  // Crear la base de una cuenta tarda más que los 30 s de siempre (26/10: systec se creó bien y la pantalla dijo que no)
  create: (data) => api.post('/tenants', data, { timeout: 180000 }),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  suspend: (id, reason) => api.post(`/tenants/${id}/suspend`, { reason }),
  activate: (id) => api.post(`/tenants/${id}/activate`),
  delete: (id, confirmation) => api.delete(`/tenants/${id}`, { data: { confirmation } }),
  resetPassword: (id) => api.post(`/tenants/${id}/reset-password`),
  impersonate: (id) => api.post(`/tenants/${id}/impersonate`)
};

// Metrics API
export const metricsApi = {
  dashboard: () => api.get('/metrics/dashboard'),
  trend: (days = 30) => api.get('/metrics/trend', { params: { days } }),
  refresh: (id) => api.post(`/metrics/refresh/${id}`),
  refreshAll: () => api.post('/metrics/refresh-all')
};

// Logs API
export const logsApi = {
  audit: (params) => api.get('/logs/audit', { params }),
  tenantLogs: (id, params) => api.get(`/logs/tenant/${id}`, { params }),
  actions: () => api.get('/logs/actions')
};

// Consola: datos en vivo
export const panelApi = {
  resumen: (forzar = false) => api.get('/panel/resumen', { params: forzar ? { forzar: 1 } : {} }),
  cuenta: (id, forzar = false) => api.get(`/panel/cuentas/${id}`, { params: forzar ? { forzar: 1 } : {} }),
  rechazos: (params) => api.get('/panel/rechazos', { params }),
  seguimiento: (tenantId, documentoId, data) => api.put(`/panel/rechazos/${tenantId}/${documentoId}`, data),
  inicioOperaciones: () => api.get('/panel/inicio-operaciones'),
  guardarInicioOperaciones: (fecha) => api.put('/panel/inicio-operaciones', { fecha }),
  alertas: () => api.get('/panel/alertas'),
  guardarAlertas: (data) => api.put('/panel/alertas', data),
  probarAlertas: () => api.post('/panel/alertas/probar', {}, { timeout: 60000 }),
  planes: () => api.get('/panel/planes'),
  guardarPlan: (codigo, data) => api.put(`/panel/planes/${codigo}`, data),
  modulosAvanzados: () => api.get('/panel/modulos-avanzados', { timeout: 60000 }),
  moduloAvanzado: (id) => api.get(`/panel/modulos-avanzados/${id}`, { timeout: 60000 }),
  asignarModulo: (id, empresas) => api.post(`/panel/modulos-avanzados/${id}/asignar`, { empresas }),
  quitarModulo: (id, tenantId, emisorId) => api.delete(`/panel/modulos-avanzados/${id}/empresas/${tenantId}/${emisorId}`),
  aparienciaLista: () => api.get('/panel/apariencia', { timeout: 60000 }),
  aparienciaCuenta: (id) => api.get(`/panel/cuentas/${id}/apariencia`),
  cambiarAparienciaCuenta: (id, tema) => api.put(`/panel/cuentas/${id}/apariencia`, { tema }),
  aparienciaTodas: (tema) => api.put('/panel/apariencia/todas', { tema }, { timeout: 120000 })
};

// Respaldos (todos los tenants)
export const respaldosApi = {
  config: () => api.get('/respaldos/config'),
  guardarConfig: (data) => api.put('/respaldos/config', data),
  resumen: () => api.get('/respaldos/resumen'),
  logs: (params) => api.get('/respaldos/logs', { params }),
  respaldarTenant: (tenantId) => api.post(`/respaldos/tenant/${tenantId}`),
  respaldarTodos: () => api.post('/respaldos/todos'),
  descargar: (id) => api.get(`/respaldos/${id}/descargar`, { responseType: 'blob', timeout: 0 }),
  restaurar: (id, confirmacion) => api.post(`/respaldos/${id}/restaurar`, { confirmacion }),
  eliminar: (id) => api.delete(`/respaldos/${id}`),
  eliminarVarios: (ids) => api.post('/respaldos/eliminar-varios', { ids }),
  aplicarRetencion: () => api.post('/respaldos/retencion'),
  analisis: () => api.get('/respaldos/analisis'),
  aplicarAnalisis: () => api.post('/respaldos/analisis/aplicar', {}, { timeout: 120000 }),
  clasificarTabla: (tabla, clase) => api.post('/respaldos/analisis/clasificar', { tabla, clase })
};

export default api;
